import Loading from '@cartwhl/ui/atoms/Loading';
import Text from '@cartwhl/ui/base/Text';
import CheckmarkIcon from '@cartwhl/ui/icons/CheckmarkIcon';
import Button from '@cartwhl/ui/input/Button';
import PlayerDialog from '@cartwhl/ui/molecules/PlayerDialog';

import { useStore, withProvider } from '../batchGenerationPromptStore';
import ExportOptionsSection from './ExportOptionsSection';
import ModelAndSettingsSection from './ModelAndSettingsSection';
import MotionPromptSection from './MotionPromptSection';
import RecentGenerationJobs from './RecentGenerationJobs';

function UploadScreen() {
  const store = useStore();
  const { promptRows, uploadModalState } = store.state;

  const hasValidPrompt = promptRows.some((row) => row.prompt.trim().length > 0);
  const hasValidVariations =
    promptRows.length > 0 && promptRows.every((row) => row.variationCount >= 1);
  const canRunProcess = hasValidPrompt && hasValidVariations;

  const isModalVisible = uploadModalState !== 'idle';

  return (
    <div className="flex flex-col">
      <div className="container mx-auto flex flex-col px-4 pt-12 pb-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 shrink-0">
          <Text
            variant="studio/body/md"
            className="text-secondary max-w-[602px]"
          >
            Our text-to-motion models generate high-quality, editable 3D motion
            from natural language—delivered in seconds.
          </Text>
        </div>

        {/* Main content - Two columns */}
        <div className="bg-secondary flex flex-col rounded-2xl p-3 sm:p-4">
          <div className="grid grid-cols-1 gap-0 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
            {/* Step 1: Add motions */}
            <div className="bg-primary border-primary flex flex-col gap-4 rounded-t-lg border p-6 sm:gap-5 sm:p-8 lg:rounded-none lg:rounded-l-lg lg:pr-6">
              <div className="flex shrink-0 items-center gap-2">
                <Text
                  variant="studio/body/medium/24"
                  className="text-primary"
                  as="h2"
                >
                  <span className="mr-2">1.</span>Add motions
                </Text>
                {hasValidPrompt && (
                  <CheckmarkIcon className="text-green h-6 w-6 shrink-0" />
                )}
              </div>

              <ModelAndSettingsSection />
              <MotionPromptSection />
            </div>

            {/* Step 2: Set your export options */}
            <div className="bg-primary border-primary flex flex-col gap-5 rounded-b-lg border-r border-b border-l p-6 sm:gap-6 sm:p-8 lg:rounded-none lg:rounded-r-lg lg:border-t lg:border-l-0">
              <Text
                variant="studio/body/medium/24"
                className="text-primary"
                as="h2"
              >
                <span className="mr-2">2.</span>Set your export options
              </Text>
              <div className="flex flex-col gap-2">
                <ExportOptionsSection />
              </div>
            </div>
          </div>

          {/* Run process button */}
          <div className="mt-3 flex shrink-0 justify-end sm:mt-4">
            <Button
              variant="primary"
              size="lg"
              disabled={!canRunProcess}
              onClick={() => store.runProcess()}
            >
              Run process
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto mt-12 px-4 sm:px-6 lg:px-8">
        <div className="border-gray-30 dark:border-gray-75 border-t" />
      </div>
      <RecentGenerationJobs />

      {/* Upload/Processing Modal */}
      {isModalVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <PlayerDialog maxWidth={280}>
            <div className="relative flex flex-col items-center pb-2">
              <div className="flex w-full flex-col items-center px-4">
                <Loading className="text-green h-12 w-12" />
                <Text variant="studio/body/sm" className="mt-5 text-center">
                  Starting process...
                </Text>
                <Text variant="studio/body/xs" className="text-tertiary mt-4 text-center">
                  This may take a moment…
                </Text>
              </div>
            </div>
          </PlayerDialog>
        </div>
      )}
    </div>
  );
}

export const UploadScreenWithStore = withProvider(UploadScreen);
export default UploadScreen;
